import { useId, useState } from "react";
import { NewFeatureBadge } from "./new-feature-badge";
import { ChevronDown, Plus, Ruler, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { MeasurementReviewField } from "@/lib/measurement-review";
import { shortMeasurementReviewLabel } from "@/lib/measurement-review-label";
import { measurementDisplay, updateReviewMeasurement, withoutDuplicateCalculatedFootprint } from "@/lib/extension-review-measurements";
import { groupMeasurementReviewFields } from "@/lib/measurement-review-groups";

const UNITS = ["m", "m²", "m³", "each", "kg", "kg/m"] as MeasurementReviewField["unit"][];

function provenance(field: MeasurementReviewField): { label: string; tone: string } | null {
  if (field.calculation) return { label: "Calculated", tone: "bg-primary/15 text-foreground" };
  const o = field.original;
  if (field.descriptionBasis && !o && !field.edited && field.value.trim()) return {
    label: field.descriptionBasis === "assumed" ? "AI assumption" : "From description",
    tone: field.descriptionBasis === "assumed" ? "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100" :
      "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
  };
  if (!o || field.custom) return field.value.trim() || field.custom ? { label: "Your change", tone: "bg-primary/15 text-foreground" } : null;
  if (!field.value.trim() || Number(field.value) !== o.value) return { label: "Your change", tone: "bg-primary/15 text-foreground" };
  if (field.drawingLimit) return { label: /\bmin(?:imum)?\b/i.test(`${o.label} ${o.note}`) ? "Drawing minimum" : "Drawing maximum",
    tone: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100" };
  if (o.source === "measured") return { label: "AI reading", tone: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100" };
  if (o.source === "assumed") return { label: "AI assumption", tone: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100" };
  return null;
}

export interface MeasurementReviewPanelProps {
  fields: MeasurementReviewField[];
  onFieldsChange(fields: MeasurementReviewField[]): void;
  onAddMeasurement(): void;
  isPending?: boolean;
  jobLabel?: string;
  error?: string;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  onConfirm?(): void;
  confirmLabel?: string;
  generatesQuote?: boolean;
  inlineOpen?: boolean;
  onInlineOpenChange?(open: boolean): void;
}

export function MeasurementReviewPanel({
  fields, onFieldsChange, onAddMeasurement, isPending = false, error,
  open, onOpenChange, onConfirm, confirmLabel = "Confirm measurements",
  generatesQuote = false,
  inlineOpen, onInlineOpenChange,
}: MeasurementReviewPanelProps) {
  const [localOpen, setLocalOpen] = useState(false);
  const [localInlineOpen, setLocalInlineOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const [editingGroups, setEditingGroups] = useState<ReturnType<typeof groupMeasurementReviewFields> | null>(null);
  const editorId = useId();
  const inlineExpanded = inlineOpen ?? localInlineOpen;
  const setInlineExpanded = (next: boolean) => {
    if (onInlineOpenChange) onInlineOpenChange(next);
    else setLocalInlineOpen(next);
  };
  const expanded = open ?? localOpen;
  const setExpanded = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setLocalOpen(next);
  };
  const update = (id: string, patch: Partial<MeasurementReviewField>) =>
    onFieldsChange(updateReviewMeasurement(fields, id, patch));
  const remove = (id: string) => onFieldsChange(
    updateReviewMeasurement(fields, id, { removed: true }).filter(f => f.id !== id || !f.custom));
  const visibleFields = withoutDuplicateCalculatedFootprint(fields).filter(f => !f.removed);
  const currentFields = new Map(visibleFields.map(f => [f.id, f]));
  const groups = editingGroups
    ? editingGroups.map(g => ({ ...g, fields: g.fields.flatMap(f => {
        const current = currentFields.get(f.id);
        return current ? [current] : [];
      }) })).filter(g => g.fields.length)
    : groupMeasurementReviewFields(visibleFields);

  // One editor and one state source, shared by the first pop-out and later inline edits.
  const editor = <>
    <div id="measurement-panel-fields">
    {visibleFields.length === 0 ? (
      <p className="mt-3 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground" data-testid="empty-measurements">
        No sizes yet. Add any you know.
      </p>
    ) : <div className="mt-3 space-y-3" data-testid="list-measurements">
      {groups.map((group, groupIndex) => {
        const groupOpen = !collapsedGroups.has(group.id);
        const contentId = `${editorId}-group-${groupIndex}`;
        return <section key={group.id} className="min-w-0 rounded-md border p-2" data-testid="measurement-location-group">
          <h4>
            <button type="button" aria-expanded={groupOpen} aria-controls={contentId}
              onClick={() => setCollapsedGroups(previous => {
                const next = new Set(previous);
                if (next.has(group.id)) next.delete(group.id);
                else next.add(group.id);
                return next;
              })}
              className="flex min-h-9 w-full items-center gap-2 rounded-sm text-left text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="min-w-0 flex-1 break-words">{group.label}</span>
              <span className="shrink-0 text-muted-foreground">{group.fields.length}</span>
              <ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 transition-transform ${groupOpen ? "rotate-180" : ""}`} />
            </button>
          </h4>
          <ul id={contentId} hidden={!groupOpen} className={`mt-2 min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 ${groupOpen ? "grid" : "hidden"}`}>
      {group.fields.map(f => {
        const i = visibleFields.indexOf(f);
        const display = measurementDisplay(f);
        const p = provenance(f);
        const label = f.label.trim() || `Measurement ${i + 1}`;
        const footingAxis = /\b(?:footings?|foundations?|trench(?:es)?)\b/i.test(label) &&
          label.match(/\b(length|width|depth)\b/i)?.[1];
        const displayLabel = f.custom ? label : footingAxis
          ? `Footing (${footingAxis[0].toUpperCase()}${footingAxis.slice(1).toLowerCase()})`
          : shortMeasurementReviewLabel(f.reviewLabel || label);
        const name = displayLabel;
        const technicalName = f.subjectLabel && !label.startsWith(f.subjectLabel)
          ? `${f.subjectLabel} — ${label}` : label;
        const valueId = `measurement-value-${f.id}`;
        const note = f.drawingLimit ? f.note : f.original?.note || f.note;
        return <li key={f.id} className="min-w-0 rounded-md border bg-background p-2" data-testid={`row-measurement-${f.id}`}
          onFocusCapture={() => { if (!editingGroups) setEditingGroups(groups); }}
          onBlurCapture={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEditingGroups(null);
          }}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {f.custom ? (
              <input aria-label={`Name of measurement ${i + 1}`} value={f.label} disabled={isPending}
                placeholder="e.g. Hallway length" data-testid={`input-measurement-label-${f.id}`}
                onChange={e => update(f.id, { label: e.target.value })}
                className="h-7 min-w-0 flex-1 rounded border border-input bg-background px-2 text-xs font-medium" />
                ) : <label htmlFor={valueId} className="min-w-0 flex-1 break-words text-xs font-medium" title={technicalName}>
                  {displayLabel}
                </label>}
            {p && <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${p.tone}`} data-testid={`status-provenance-${f.id}`}>{p.label}</span>}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <input id={valueId} type="number" min="0" step={display.millimetres ? "1" : "any"}
              inputMode={display.millimetres ? "numeric" : "decimal"}
              value={display.value}
              aria-label={`${name} value`} disabled={isPending}
              data-testid={`input-measurement-value-${f.id}`}
              onChange={e => update(f.id, { value: display.millimetres && e.target.value.trim() !== ""
                ? String(Number(e.target.value) / 1000) : e.target.value })}
              className="h-8 w-24 min-w-0 rounded border border-input bg-background px-2 font-mono text-sm tabular-nums" />
            {f.custom ? (
              <select aria-label={`${name} unit`} value={f.unit} disabled={isPending} data-testid={`select-measurement-unit-${f.id}`}
                onChange={e => update(f.id, {
                  unit: e.target.value as MeasurementReviewField["unit"],
                  value: e.target.value === f.unit ? f.value : "",
                })}
                className="h-8 rounded border border-input bg-background px-1.5 text-sm">
                {UNITS.map(u => <option key={u} value={u}>{u === f.unit ? display.unit : u === "m" ? "mm" : u}</option>)}
              </select>
            ) : <span className="text-sm text-muted-foreground" data-testid={`text-measurement-unit-${f.id}`}>{display.unit}</span>}
            <div className="ml-auto flex items-center">
              {f.value !== "" && <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={isPending}
                onClick={() => update(f.id, { value: "" })} data-testid={`button-clear-measurement-${f.id}`}
                aria-label={`Clear ${name} value`}>Clear</Button>}
              {<Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={isPending}
                onClick={() => remove(f.id)} aria-label={`Remove ${name}`} data-testid={`button-remove-measurement-${f.id}`}>
                <X aria-hidden="true" className="h-4 w-4" /></Button>}
            </div>
          </div>
          {(note || !f.custom) && <details className="mt-1 text-[11px] text-muted-foreground" data-testid={`details-measurement-note-${f.id}`}>
            <summary className="cursor-pointer select-none">Source</summary>
            {!f.custom && <p className="mt-0.5 break-words">{technicalName}</p>}
            {note && <p className="mt-0.5 break-words">{note}</p>}
          </details>}
        </li>;
      })}
          </ul>
        </section>;
      })}
    </div>}

    <Button type="button" variant="outline" size="sm" className="mt-2 h-8 border-dashed text-xs" disabled={isPending}
      onClick={onAddMeasurement} data-testid="button-add-measurement">
      <Plus aria-hidden="true" className="mr-1 h-3.5 w-3.5" />Add measurement
    </Button>
    </div>

    {error && <p role="alert" className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="text-measurement-error">{error}</p>}
  </>;
  const confirmButton = <Button type="button" onClick={() => {
    if (onConfirm) onConfirm();
    else { setExpanded(false); setInlineExpanded(false); }
  }} disabled={isPending} data-testid="button-confirm-measurements">{confirmLabel}</Button>;
  const inlineVisible = inlineExpanded && !expanded;

  return <section aria-labelledby="measurement-panel-title" className="rounded-lg border bg-card/60 p-3 sm:p-4" data-testid="panel-measurement-review">
    <h3 id="measurement-panel-title" className="w-full text-sm font-semibold">
      <button type="button" aria-expanded={inlineExpanded}
        aria-controls="measurement-inline-editor"
        onClick={() => setInlineExpanded(!inlineExpanded)}
        data-testid="button-toggle-measurement-review"
        className="flex w-full items-center gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Ruler aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          <span>Review Measurements</span>
          <NewFeatureBadge testId="badge-measurement-review-new" />
          <span className="text-xs font-semibold text-muted-foreground leading-tight">(Optional · Check sizes)</span>
        </span>
        <ChevronDown aria-hidden="true" className={`ml-auto h-4 w-4 transition-transform ${inlineVisible ? "rotate-180" : ""}`} />
      </button>
    </h3>
    {inlineVisible && <div id="measurement-inline-editor" data-testid="measurement-inline-editor">
      <div className="sticky top-2 z-10 mt-3 flex flex-wrap justify-end gap-2 rounded-md border bg-card p-2">
        <Button type="button" variant="outline" onClick={() => setInlineExpanded(false)}>Collapse</Button>
        {confirmButton}
      </div>
      {editor}
    </div>}
    <Dialog open={expanded} onOpenChange={setExpanded}>
      <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-2xl" data-testid="dialog-measurement-review">
        <DialogHeader className="shrink-0">
          <DialogTitle>Review Measurements</DialogTitle>
          <DialogDescription>{generatesQuote
            ? "Check the sizes, then confirm to create your quote."
            : "Add any sizes you know, then confirm to return to the project. Close without adding measurements to skip this step; you won't be asked again. This does not generate a quote."} Leave unknown measurements blank. Lengths use mm; footing lengths use linear metres.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto pr-1" data-testid="measurement-dialog-scroll">
          {editor}
        </div>
      <DialogFooter className="shrink-0 border-t pt-3" data-testid="measurement-dialog-actions">
        <Button type="button" variant="outline" onClick={() => setExpanded(false)} disabled={isPending}>Skip for now</Button>
        {confirmButton}
      </DialogFooter>
      </DialogContent>
    </Dialog>
  </section>;
}
